import React from 'react';
import PropTypes from 'prop-types';
import { Autoplay, FreeMode, Zoom, Pagination, Scrollbar } from 'swiper';
import { Swiper as SwiperReact, SwiperSlide } from 'swiper/react';
import { forceCheck } from 'react-lazyload';

import './Swiper.css';
import styles from './Swiper.module.scss';

const Type = {
    Default: 'default',
    Alternative: 'alternative',
};

const ArrowType = {
    None: 'none',
    Out: 'out',
    In: 'in',
    Bottom: 'bottom',
};

const DirectionType = {
    Vertical: 'vertical',
    Horizontal: 'horizontal',
};

const ClickType = {
    Next: 'next-slide',
    Prev: 'prev-slide',
    Pagination: 'pagination',

    // Deprecated -- use Next, Prev & Pagination instead
    NEXT: 'next-slide',
    PREV: 'prev-slide',
    PAGINATION: 'pagination',
};

const getSwiperConfig = ({
    autoplay, delay, disableOnInteraction, zoom, scrollbar, bullets,
    vertical, breakpoints, multiple, speed, initial, pagination, paginationElem
}) => ({
    zoom,
    speed: speed || 500,
    initialSlide: initial,
    direction: vertical ? DirectionType.Vertical : DirectionType.Horizontal,
    breakpoints: /* istanbul ignore next */ breakpoints && Object.keys(breakpoints)?.reduce((p, n) => ({
        ...p, [n]: {
            ...breakpoints[n],
            freeMode: {
                enabled: !!breakpoints[n]?.freeMode,
            },
        },
    }), {}),
    slidesPerView: multiple ? 'auto' : 1,
    // slidesPerGroupAuto: true,
    watchSlidesProgress: true, // need to prevent images to be loaded when not in viewport
    autoplay: /* istanbul ignore next */ autoplay && ({
        delay: delay || 5000,
        disableOnInteraction: !disableOnInteraction,
    }),
    scrollbar: scrollbar && ({
        draggable: true,
        hide: false,
    }),
    pagination: pagination && ({
        el: paginationElem?.current,
        clickable: true,
        dynamicBullets: !!bullets,
        dynamicMainBullets: bullets || 1,    
    }),
});

const getId = () => `swiper_${parseInt(Math.random() * 1000)}`;

const getClasses = ({ vertical, arrows, multiple, scrollbar, type }) => [
    styles['swiper-component'],
    vertical ? styles['swiper-vertical'] : '',
    arrows ? styles[`swiper-arrows-${arrows}`] : styles['swiper-arrows-out'],
    multiple ? styles['swiper-multiple-items'] : '',
    scrollbar ? styles['swiper-scrollbar-out'] : '',
    styles[type || Type.Default],
].join(' ');

const Swiper = ({
    id, type, breakpoints, initial = 0, arrows, scrollbar, vertical,
    pagination, multiple, autoplay, disableOnInteraction, zoom, bullets,
    loop, children, click, onClick = click, lazy, delay, speed,
}) => {

    const paginationElem = React.useRef(null);

    const [generatedId] = React.useState(id || getId());
    const [swiper, setSwiper] = React.useState(null);
    const [classes, setClasses] = React.useState(getClasses({ vertical, arrows, multiple, scrollbar, type }));
    const [hideArrows, setHideArrows] = React.useState({ prev: true, next: true });

    /* istanbul ignore next */
    const updateSlider = (swiper) => {
        if (!swiper) return;
        setClasses(getClasses({ vertical, arrows, multiple, scrollbar, type }));
        setArrowsVisibility(swiper);
        swiper.update();
    };

    const handleSlidePrev = () => {
        /* istanbul ignore if */
        if (zoom) swiper?.zoom?.out();
        /* istanbul ignore else */
        if (!loop) swiper?.slidePrev();
        else swiper?.slideTo((swiper.activeIndex - 1 + children.length) % children.length);
        onClick && onClick(ClickType.PREV);
    };

    const handleSlideNext = () => {
        /* istanbul ignore if */
        if (zoom) swiper?.zoom?.out();
        /* istanbul ignore else */
        if (!loop) swiper?.slideNext();
        else swiper?.slideTo((swiper.activeIndex + swiper.params.slidesPerGroup) % children.length);
        onClick && onClick(ClickType.NEXT);
    };

    const handleClickPagination = () => onClick && onClick(ClickType.PAGINATION);

    /* istanbul ignore next */
    const setArrowsVisibility = (swiper) => setHideArrows({
        prev: !swiper?.activeIndex && !loop,
        next: (swiper?.activeIndex || 0) + (swiper?.params?.slidesPerView || 1) >= swiper?.slides.length && !loop,
    });

    React.useEffect(() => {
        updateSlider(swiper);
    }, [vertical, arrows, multiple, scrollbar]);

    return (
        <div id={generatedId} className={classes}>
            <SwiperReact
                modules={[Autoplay, FreeMode, Zoom, Pagination, Scrollbar]}
                {...getSwiperConfig({
                    autoplay, delay, disableOnInteraction, zoom, scrollbar, bullets,
                    vertical, breakpoints, multiple, speed, initial, pagination, paginationElem,
                })}
                onInit={forceCheck}
                onSliderMove={forceCheck}
                onTransitionStart={forceCheck}
                onSwiper={setSwiper}
                onSlideChange={() => {
                    setArrowsVisibility(swiper);
                    /* istanbul ignore if */
                    if (zoom) swiper?.zoom?.out();
                }}
            >
                {
                    (children || []).map((slide, i) => (
                        <SwiperSlide key={slide.key}>
                            {!lazy || swiper?.activeIndex >= i - 1 && swiper?.activeIndex <= i + 1 ? slide : null}
                        </SwiperSlide>
                    ))
                }

            </SwiperReact>

            {
                pagination && (
                    <div
                        ref={paginationElem}
                        className={styles[`swiper-pagination`]}
                        onClick={handleClickPagination}
                    />
                )
            }

            {
                ArrowType.None !== arrows && (
                    <>
                        <div
                            className={`${styles['swiper-button-prev']} ${hideArrows.prev ? styles.hidden : ''}`}
                            data-testid="swiper-prev"
                            onClick={handleSlidePrev}
                        />
                        <div
                            className={`${styles['swiper-button-next']} ${hideArrows.next ? styles.hidden : ''}`}
                            data-testid="swiper-next"
                            onClick={handleSlideNext}
                        />
                    </>
                )
            }

        </div>
    );
};

Swiper.propTypes = {
    id: PropTypes.string,
    type: PropTypes.oneOf([
        Type.Default,
        Type.Alternative,
    ]),
    breakpoints: PropTypes.shape({
        slidesPerView: PropTypes.number,
        slidesPerGroup: PropTypes.number,
        spaceBetween: PropTypes.number,
        freeMode: PropTypes.number,
    }),
    initial: PropTypes.number,
    arrows: PropTypes.oneOf([
        ArrowType.None,
        ArrowType.Out,
        ArrowType.In,
        ArrowType.Bottom,
    ]),
    scrollbar: PropTypes.bool,
    vertical: PropTypes.bool,
    pagination: PropTypes.bool,
    bullets: PropTypes.number,
    multiple: PropTypes.bool,
    autoplay: PropTypes.bool,
    // !! If true, interaction is not disabled
    disableOnInteraction: PropTypes.bool,
    zoom: PropTypes.bool,
    loop: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.element),
    lazy: PropTypes.bool,
    delay: PropTypes.number,
    speed: PropTypes.number,
    onClick: PropTypes.func,
    // Deprecated -- use onClick instead
    click: PropTypes.func,
};

export default Swiper;

export {
    Type as SwiperType,
    ClickType as SwiperClick,
    ArrowType as SwiperArrowType,
};
